<script setup>
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from "reka-ui";
import { computed, ref } from "vue";

import BaseSpinner from "./BaseSpinner.vue";
import FormField from "./FormField.vue";
import SvgIcon from "./SvgIcon.vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: "Select..." },
  searchPlaceholder: { type: String, default: "Search..." },
  searchable: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  // Spinner in the trigger only; the options stay usable (e.g. LinkInput's background check).
  loading: { type: Boolean, default: false },
  // The option list is still being fetched: trigger spinner plus a loading row in the open list.
  loadingOptions: { type: Boolean, default: false },
  labelProp: { type: String, default: "label" },
  valueProp: { type: String, default: "value" },
  label: { type: String, default: "" },
  id: { type: String, default: "" },
  error: { type: String, default: "" },
  inlineError: { type: Boolean, default: false },
  info: { type: String, default: "" },
  // Max height of the scrollable options list, in px.
  listMaxHeight: { type: Number, default: 240 },
});
const emit = defineEmits(["update:modelValue", "update:open"]);

const open = ref(false);
const searchTerm = ref("");

const normalizedOptions = computed(() =>
  props.options.map((option) => {
    if (
      option &&
      (option[props.labelProp] !== undefined || option[props.valueProp] !== undefined)
    ) {
      return option;
    }
    return { [props.labelProp]: option, [props.valueProp]: option };
  }),
);

const filtered = computed(() => {
  const q = searchTerm.value?.trim().toLowerCase();
  if (!q) return normalizedOptions.value;
  return normalizedOptions.value.filter((option) =>
    String(option[props.labelProp]).toLowerCase().includes(q),
  );
});

const selectedLabel = computed(() => {
  const selected = normalizedOptions.value.find(
    (option) => option[props.valueProp] === props.modelValue,
  );
  return selected ? selected[props.labelProp] : "";
});

// Reka ignores ComboboxItem with value="", so we use an internal key for each option.
// The key is the stringified value, or a fallback for empty/null values.
const EMPTY_KEY = "__combo_empty__";

function toComboboxKey(val) {
  const s = String(val ?? "");
  return s === "" ? EMPTY_KEY : s;
}

function fromComboboxKey(key) {
  return key === EMPTY_KEY ? "" : key;
}

function onOpenChange(isOpen) {
  open.value = isOpen;
  if (isOpen) searchTerm.value = "";
  emit("update:open", isOpen);
}

function onSelect(event) {
  const realVal = fromComboboxKey(event.detail.value);
  const option = normalizedOptions.value.find((o) => String(o[props.valueProp] ?? "") === realVal);
  if (option && !option.disabled) {
    emit("update:modelValue", option[props.valueProp]);
  }
}
</script>

<template>
  <FormField :id :label :error :info :inline-error>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots['label-info']" #label-info="{ id: descriptionId }">
      <slot :id="descriptionId" name="label-info" />
    </template>
    <template #default="{ describedBy }">
      <div class="combo-select">
        <ComboboxRoot :open :disabled :ignore-filter="true" @update:open="onOpenChange">
          <ComboboxAnchor class="anchor">
            <!-- reka-ui's ComboboxTrigger hardcodes tabindex="-1" (it expects a
                 focusable ComboboxInput in the anchor). We drive it trigger-only,
                 so override to keep it in the tab order for keyboard users.
                 Its hardcoded aria-label="Show popup" would also mask the field label. -->
            <ComboboxTrigger
              :id="id || undefined"
              :aria-label="
                label && selectedLabel ? `${label}: ${selectedLabel}` : label || undefined
              "
              class="trigger"
              :disabled
              :tabindex="disabled ? -1 : 0"
              :aria-describedby="describedBy"
            >
              <span class="trigger-label" :class="{ placeholder: !selectedLabel }">
                {{ selectedLabel || placeholder }}
              </span>
              <BaseSpinner v-if="loading || loadingOptions" />
              <span v-else class="trigger-arrow">▾</span>
            </ComboboxTrigger>
          </ComboboxAnchor>

          <ComboboxContent
            class="content"
            position="popper"
            :side-offset="4"
            :body-lock="false"
            :style="{ '--combo-list-max-height': `${listMaxHeight}px` }"
          >
            <div v-if="searchable" class="search-wrap">
              <SvgIcon name="system/search" width="16" height="16" class="search-icon" />
              <ComboboxInput
                v-model="searchTerm"
                :display-value="() => ''"
                :placeholder="searchPlaceholder"
                class="search-input"
                auto-focus
              />
            </div>
            <ComboboxViewport class="viewport">
              <div v-if="loadingOptions" class="no-results" role="status" aria-label="Loading">
                <BaseSpinner />
              </div>
              <ComboboxEmpty v-else class="no-results">No results</ComboboxEmpty>
              <ComboboxItem
                v-for="option in filtered"
                :key="toComboboxKey(option[valueProp])"
                :value="toComboboxKey(option[valueProp])"
                :disabled="option.disabled"
                class="option"
                :class="{ selected: option[valueProp] === modelValue }"
                @select="onSelect"
              >
                <span class="option-label">{{ option[labelProp] }}</span>
              </ComboboxItem>
            </ComboboxViewport>
          </ComboboxContent>
        </ComboboxRoot>
      </div>
    </template>
  </FormField>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;

.combo-select {
  width: 100%;

  :deep(> *) {
    width: 100%;
  }
}

.anchor {
  width: 100%;
}

.trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 36px;
  padding: 0 0.5rem 0 0.75rem;
  font-family: inherit;
  font-size: 0.8125rem;
  color: c.$text;
  background-color: c.$background-ui;
  border: 1px solid c.$border-light-color;
  border-radius: 6px;
  outline: none;
  cursor: pointer;
  gap: 4px;

  &:hover:not(:focus, [data-state="open"], :disabled) {
    border-color: c.$text-subtle;
  }

  &:focus,
  &[data-state="open"] {
    border-color: c.$main;
  }

  &:disabled {
    background-color: c.$grey-lightest;
    cursor: not-allowed;
    color: c.$text-disabled;
  }
}

.trigger-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.placeholder {
    color: c.$text-placeholder;
  }
}

.trigger-arrow {
  color: c.$text-subtle;
  font-size: 0.875rem;
  flex-shrink: 0;
}
</style>

<style lang="scss">
@use "@/styles/layers";
@use "@/colors" as c;

/* Unscoped — required because ComboboxContent renders in a portal outside this component */
.combo-select .content {
  width: var(--reka-combobox-trigger-width);
  background: c.$white;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgb(5 0 56 / 12%);
  z-index: layers.$floating;
  overflow: hidden;
}

.combo-select .search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px;
}

.combo-select .search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: c.$text-subtle;
  pointer-events: none;
}

.combo-select .search-input {
  width: 100%;
  height: 32px;
  padding: 0 8px 0 32px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: c.$text;
  background-color: c.$background-ui;
  border: 1px solid c.$border-subtle;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: c.$text-placeholder;
  }

  &:focus {
    border-color: c.$main;
  }
}

.combo-select .viewport {
  padding: 4px;
  max-height: var(--combo-list-max-height, 240px);
  overflow-y: auto;
}

.combo-select .option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 8px;
  font-size: 0.8125rem;
  color: c.$text;
  cursor: pointer;
  border-radius: 4px;
  outline: none;
  user-select: none;

  &[data-highlighted] {
    background-color: c.$background-grey-light;
  }

  &.selected {
    background-color: c.$main;
    color: c.$text-inverse;
  }

  &[data-disabled] {
    color: c.$text-disabled;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.combo-select .option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.combo-select .no-results {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  font-size: 0.8125rem;
  color: c.$text-subtle;
}
</style>
